/* ========================================================================== 
   Blink Box - Product Also Available analytics
   Markup source: snippets/product-also-available.liquid
========================================================================== */
(function () {
  'use strict';

  if (window.__blinkboxAlsoAvailableAnalyticsLoaded) return;
  window.__blinkboxAlsoAvailableAnalyticsLoaded = true;

  const SELECTORS = {
    component: '[data-also-available]',
    toggle: '[data-also-available-toggle]',
    link: '[data-also-available-link]',
    item: '[data-also-available-item]'
  };

  const initialised = new WeakSet();
  const viewTracked = new WeakSet();
  const viewTimers = new WeakMap();

  function getGroupSizeBucket(count) {
    if (count <= 1) return '1';
    if (count <= 4) return '2-4';
    if (count <= 8) return '5-8';
    if (count === 9) return '9';
    if (count <= 14) return '10-14';
    return '15+';
  }

  function publishShopifyEvent(name, payload) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    try {
      const result = window.Shopify.analytics.publish(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      // Analytics must never interrupt the shopping experience.
    }
  }

  function sendGoogleEvent(name, params) {
    if (typeof window.gtag !== 'function') return;

    try {
      window.gtag('event', name, params);
    } catch (error) {
      // Google measurement is optional.
    }
  }

  function buildBasePayload(component) {
    const groupProductCount = Number(component.dataset.groupProductCount || 0);

    return {
      vendor: component.dataset.currentProductVendor || '',
      relationship_type: component.dataset.relationshipType || '',
      product_group_id: component.dataset.productGroupId || '',
      product_group_title: component.dataset.productGroupTitle || '',
      group_product_count: groupProductCount,
      eligible_product_count: Number(component.dataset.eligibleCount || 0),
      group_size_bucket: getGroupSizeBucket(groupProductCount),
      source_product_id: component.dataset.currentProductId || '',
      source_product_name: component.dataset.currentProductTitle || '',
      source_product_handle: component.dataset.currentProductHandle || ''
    };
  }

  function track(component, eventName, extra = {}) {
    const payload = {
      ...buildBasePayload(component),
      ...extra
    };

    publishShopifyEvent(`blinkbox_${eventName}`, payload);
    sendGoogleEvent(eventName, payload);
  }

  function getExploredStorageKey(groupId) {
    return `blinkboxAlsoAvailableExplored:${groupId}`;
  }

  function getNavigationDepth(component, selectedProductId) {
    const groupId = component.dataset.productGroupId || '';
    const sourceProductId = component.dataset.currentProductId || '';
    if (!groupId) return 0;

    const storageKey = getExploredStorageKey(groupId);
    let explored = [];

    try {
      const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) explored = stored;
    } catch (error) {
      explored = [];
    }

    const distinctIds = new Set(explored.map(String));
    if (sourceProductId) distinctIds.add(String(sourceProductId));
    if (selectedProductId) distinctIds.add(String(selectedProductId));

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(Array.from(distinctIds)));
    } catch (error) {
      // sessionStorage can be unavailable in some privacy contexts.
    }

    return distinctIds.size;
  }

  function trackView(component) {
    if (viewTracked.has(component)) return;

    const timer = viewTimers.get(component);
    if (timer) window.clearTimeout(timer);

    viewTimers.delete(component);
    viewTracked.add(component);
    track(component, 'also_available_view');
  }

  function initViewTracking(component) {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (viewTracked.has(component)) {
          observer.disconnect();
          return;
        }

        if (entry.intersectionRatio >= 0.6) {
          if (viewTimers.has(component)) return;

          const timer = window.setTimeout(() => {
            trackView(component);
            observer.disconnect();
          }, 500);

          viewTimers.set(component, timer);
        } else {
          const timer = viewTimers.get(component);
          if (timer) window.clearTimeout(timer);
          viewTimers.delete(component);
        }
      });
    }, { threshold: [0, 0.6] });

    observer.observe(component);
  }

  function handleClick(event, component) {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest(SELECTORS.toggle);
    if (toggle && component.contains(toggle)) {
      const isOpening = toggle.getAttribute('aria-expanded') !== 'true';
      if (isOpening) track(component, 'also_available_expand');
      return;
    }

    const link = event.target.closest(SELECTORS.link);
    if (!link || !component.contains(link)) return;

    const item = link.closest(SELECTORS.item);
    const selectedProductId = link.dataset.productId || '';

    track(component, 'also_available_select', {
      selected_product_id: selectedProductId,
      selected_product_name: link.dataset.productTitle || '',
      selected_product_handle: link.dataset.productHandle || '',
      selected_product_vendor: link.dataset.productVendor || '',
      selected_position: Number(link.dataset.productPosition || 0),
      selected_was_hidden: item?.hasAttribute('data-hidden-initially') ? 'true' : 'false',
      group_navigation_depth: getNavigationDepth(component, selectedProductId)
    });
  }

  function initComponent(component) {
    if (!component || initialised.has(component)) return;

    initialised.add(component);
    component.dataset.alsoAvailableAnalyticsInitialised = 'true';

    component.addEventListener('click', (event) => {
      handleClick(event, component);
    }, true);

    initViewTracking(component);
  }

  function boot(scope) {
    const root = scope || document;

    if (root instanceof Element && root.matches(SELECTORS.component)) {
      initComponent(root);
    }

    root.querySelectorAll?.(SELECTORS.component).forEach(initComponent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });
})();
